<template>
  <custom-modal
    v-model="showAddProfileDialog"
    :confirm-button-loading="isLoading"
    @confirm="saveSocialMediaProfile"
    @cancel="$emit('close')"
  >
  <template v-slot:title>{{ formAction }} Social Media Profile</template>

    <div class="form-container">
      <!-- Profile Name -->
      <div class="mb-4">
        <label class="form-label">Profile Name</label>
        <input
          type="text"
          name="name"
          v-model="form.name"
          class="form-control"
          placeholder="Enter profile name"
        >
      </div>

      <!-- Social Media Platforms -->
      <div v-for="(url, platform) in form.social_media_urls" :key="platform" class="platform-group">
        <label class="platform-label">{{ capitalize(platform) }}</label>
        <div class="input-group">
          <input
            type="url"
            v-model="form.social_media_urls[platform]"
            class="form-control"
            :placeholder="`${capitalize(platform)} URL`"
          >
          <button class="btn btn-danger remove-btn" @click="removePlatform(platform)">
            <i class="fas fa-trash-alt"></i> Remove
          </button>
        </div>
      </div>

      <!-- Add New Platform -->
      <div class="mt-4">
        <label class="form-label">Add New Platform</label>
        <div class="input-group">
          <input
            type="text"
            v-model="newPlatform"
            class="form-control"
            placeholder="Platform (e.g., LinkedIn, TikTok)"
          >
          <button
            class="btn btn-primary add-btn"
            @click="addPlatform"
            :disabled="!newPlatform"
          >
            <i class="fas fa-plus"></i> Add Platform
          </button>
        </div>
      </div>

      <!-- Error Message -->
      <base-alert v-if="form.has_error" type="danger" class="mt-3">
        <span>{{ form.error_message }}</span>
      </base-alert>
    </div>
  </custom-modal>
</template>

<script>
import axios from "axios";
import { BaseAlert } from '@/components';
import CustomModal from "../components/CustomModal.vue";

export default {
  props: {
    showAddProfileDialog: { type: Boolean, default: false },
    editProfileData: { type: Object, default: null }
  },
  components: { CustomModal, BaseAlert },
  data() {
    return {
      isLoading: false,
      newPlatform: '',
      defaultPlatforms: ['facebook', 'twitter', 'instagram', 'youtube'],
      form: {
        id: null,
        name: '',
        social_media_urls: {},
        has_error: false,
        error_message: '',
      }
    };
  },
  computed: {
    formAction() {
      return this.form.id ? 'Edit' : 'Add';
    }
  },
  watch: {
    editProfileData(value) {
      if (value) {
        // Set form data for edit mode
        this.form = {
          id: value.id,
          name: value.name,
          social_media_urls: {...value.social_media_urls},
          has_error: false,
          error_message: ''
        };

        // Add any default platforms that don't exist in the current data
        this.addMissingDefaultPlatforms();
      } else {
        // Reset form for add mode
        this.resetForm();
      }
    },
    showAddProfileDialog(value) {
      if (value && !this.editProfileData && Object.keys(this.form.social_media_urls).length === 0) {
        this.initializeDefaultPlatforms();
      }
    }
  },
  methods: {
    initializeDefaultPlatforms() {
      this.defaultPlatforms.forEach(platform => {
        this.$set(this.form.social_media_urls, platform, '');
      });
    },
    addMissingDefaultPlatforms() {
      // Only add default platforms that don't already exist
      this.defaultPlatforms.forEach(platform => {
        if (!this.form.social_media_urls.hasOwnProperty(platform)) {
          this.$set(this.form.social_media_urls, platform, '');
        }
      });
    },
    saveSocialMediaProfile() {
      this.isLoading = true;
      const apiUrl = this.form.id ? `social-media-profiles/${this.form.id}` : 'social-media-profiles';
      const method = this.form.id ? 'put' : 'post';

      axios[method](apiUrl, {
        name: this.form.name,
        social_media_urls: this.form.social_media_urls
      })
      .then(response => {
        this.isLoading = false;
        this.$toast.success(`${this.formAction}ed successfully`);
        this.$emit('close');
        this.$emit(this.form.id ? 'updated' : 'added', response.data.data);
      })
      .catch(error => {
        this.isLoading = false;
        this.form.error_message = (error.response && error.response.data && error.response.data.message) || 'An error occurred';
        this.form.has_error = true;
      });
    },
    addPlatform() {
      if (!this.newPlatform) return;

      const platformKey = this.newPlatform.toLowerCase();
      if (this.form.social_media_urls[platformKey]) {
        this.form.has_error = true;
        this.form.error_message = `${this.newPlatform} is already added`;
        return;
      }

      this.$set(this.form.social_media_urls, platformKey, '');
      this.newPlatform = '';
      this.form.has_error = false;
    },
    removePlatform(platform) {
      this.$delete(this.form.social_media_urls, platform);
    },
    capitalize(text) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    resetForm() {
      this.form = {
        id: null,
        name: '',
        social_media_urls: {},
        has_error: false,
        error_message: ''
      };

      if (this.showAddProfileDialog) {
        this.initializeDefaultPlatforms();
      }
    },
  }
}
</script>

<style scoped>
/* Styling for cleaner form */
.form-container {
  padding: 1rem;
}

.form-label {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.platform-group {
  margin-bottom: 1rem;
}

.platform-label {
  font-weight: bold;
  text-transform: capitalize;
  margin-bottom: 0.3rem;
  display: block;
}

.input-group {
  display: flex;
  align-items: center;
}

.input-group input {
  flex: 1;
  padding: 0.5rem;
  border-radius: 6px;
}

.remove-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 0.5rem;
  margin-left: 0.5rem;
}

.add-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 0.5rem;
  margin-left: 0.5rem;
}

.add-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.base-alert {
  margin-top: 1rem;
}
</style>
